<template>
  <div
    v-if="product"
    class="member-item medium-padding-bottom-z small-margin-bottom-xs large-margin-bottom-m grid-x"
    :data-test-product-id="props.productId"
  >
    <ProductTitle
      v-if="isMobile"
      :product-url="productUrl"
      class="margin-bottom-m"
      allow-opening-in-new-tab
    />
    <MarketPlaceTitle v-if="isMcomMarketplace && isMobile" />
    <div
      :id="containerId"
      class="image-container cell medium-5 large-3 large-margin-bottom-m"
    >
      <a
        :href="productUrl"
        class="product-link"
        target="_blank"
        aria-label="product link"
      >
        <Picture
          :image-url="primaryImage"
          :alt="`${productName} image`"
          v-bind="primaryImage ? { 'image-intrinsic-width': imageIntrinsicWidth } : {}"
        />
      </a>
      <template v-if="isAddToListVisible && isProductAvailable">
        <AddToList :container-id="containerId" />
      </template>
    </div>
    <div class="order-panel cell medium-6 medium-offset-1 large-8">
      <div class="product-info">
        <ProductTitle
          v-if="!isMobile"
          tag="h3"
          :product-url="productUrl"
          allow-opening-in-new-tab
        />
        <MarketPlaceTitle v-if="isMcomMarketplace && !isMobile" />
        <ProductTraits
          v-if="isProductAvailable"
          :member-product-url="productUrl"
          allow-opening-in-new-tab
        >
          <template #shop-more>
            <ShopMoreFacade v-if="isMcom && isShopMoreStandardEnabled" />
          </template>
        </ProductTraits>
        <OnDisplay
          v-if="isBigTicketItemFlag && showOnDisplay"
          :product-id="props.productId"
          condensed
        />
      </div>
      <div
        v-if="isProductAvailable"
        class="add-to-cart"
      >
        <ProtectionPlanBigTicket
          v-if="isProtectionPlanEligible"
        />

        <QuantitySelector
          v-if="isQuantityEnabled"
          v-model="quantity"
          :min="minQuantity"
          :max="maxQuantity"
          size="small"
          class="margin-bottom-m"
        />

        <template v-if="isMcom || (isBcom && !dnfPlacementEnabled)">
          <Availability
            v-if="isAvailabilityEnabled"
            class="margin-bottom-m"
            data-testid="availability"
          />

          <AvailabilityBigTicket
            v-if="isBigTicketItemFlag"
            class="margin-bottom-s"
          />
        </template>

        <BTZipCodeSlideout v-if="isBigTicketItemFlag" />
        <AutoReplenishment v-if="autoReplenishmentEnabled" />
        <CTAButtons
          v-if="callToActionButtons"
          :class="margin"
          :buttons-inline="false"
        />
        <ShippingAndReturnsBadgeFacade
          v-if="killswitches.isShippingAndReturnsBadgeEnabled"
          class="margin-bottom-m"
        />
        <ProductEngagementMcom
          v-if="productHasEngagementPill"
          class="show-for-medium"
        />

        <template v-if="isBcom && dnfPlacementEnabled">
          <Availability
            v-if="isAvailabilityEnabled"
            class="margin-bottom-m"
            data-testid="availability"
          />

          <AvailabilityBigTicket
            v-if="isBigTicketItemFlag"
            class="margin-bottom-s"
          />
        </template>
      </div>
      <UnavailableProductDetails
        v-else
        class="unavailable-details"
      />
    </div>
  </div>
</template>

<script setup>
// TODO: fix tests
/* istanbul ignore file */
import { computed, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import Picture from '@common-vue/atomic-ui/src/components/picture';
import QuantitySelector from '@atomic-ui/quantitySelector';
import { useCreateTypeStore } from '@common-vue/media-card-large/src/modules/type';
import TypeParser from '@common-vue/media-card-large/src/modules/type/parsers/TypeParser';
import { ShippingAndReturnsBadgeFacade } from '@modules/shippingAndRetunsBadge';
import {
  Availability, AvailabilityBigTicket, useAvailability, BTZipCodeSlideout,
  useBigTicketATBValidatorProvide,
  useAvailabilityBigTicketProvide,
} from '@modules/availability';
import {
  useProductProvide,
  useQuantity,
  useQuantityProvide,
} from '@modules/product';
import {
  useFeatureEligibility,
  useProductFlags,
  useKillswitches,
} from '@modules/featureControl';

import ProtectionPlanBigTicket from '@components/ProtectionPlanBigTicket';
import AddToList from '@components/AddToList';
import CTAButtons from '@components/ctaButtons/CTAButtons';
import ProductTitle from '@components/ProductCard/ProductTitle.common';
import ProductTraits from '@components/ProductCard/ProductTraits';
import MarketPlaceTitle from '@components/marketPlace/MarketPlaceTitle';
import OnDisplay from '@components/common/OnDisplay';
import UnavailableProductDetails from '@components/ProductCard/UnavailableProductDetails';
import { useUpcValidatorProvide } from '@modules/upc';
import { AutoReplenishment, useAutoReplenishmentProvide } from '@modules/auto-replenishment';

import { useImagesProvide } from '@composables/useImages';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useBrand } from '@composables/useBrand';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useMarketPlace } from '@composables/useMarketPlace';
import { useExperiments } from '@composables/useExperiments';
import { useProductPriceProvide } from '@modules/price/composables/useProductPriceProvide';
import { ShopMoreFacade } from '@modules/shop-more';

import { GET_ALL_PRODUCTS_DATA } from '../../types/getters';

const store = useStore();
const { killswitches } = useKillswitches();

const props = defineProps({
  productId: {
    type: String,
    required: true,
  },
});

const {
  experimentFlags: {
    dnfPlacementEnabled,
    isShopMoreStandardEnabled,
  },
} = useExperiments();

// console.log(props.productId);
const memberProducts = store.getters[`productStore/${GET_ALL_PRODUCTS_DATA}`];
const product = memberProducts[props.productId];
// console.log(memberProducts);
useProductProvide(props.productId, product);
useQuantityProvide();
useUpcValidatorProvide();
useMarketPlace();
useProductPriceProvide({ id: props.productId, product });
useAvailabilityBigTicketProvide();
useBigTicketATBValidatorProvide();

const ProductEngagementMcom = defineAsyncComponent(() => import(/* webpackChunkName: 'ProductEngagementMcom' */ '@components/ProductEngagement.mcom'));

const {
  callToActionButtons, isQuantityEnabled, marketPlaceItemEnabled, autoReplenishmentEnabled, productHasEngagementPill, isAddToListVisible,
} = useFeatureEligibility();
const { isBigTicketItemFlag } = useProductFlags();
const { isAvailabilityEnabled } = useAvailability();
const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();

// TODO handle in ssr for member products
// this is needed since ssr only parses for page product
useCreateTypeStore({ id: props.productId, data: TypeParser(product, { defaultToFirst: isMcom }) });

const { primaryImageData } = useImagesProvide();
// eslint-disable-next-line max-len
const { productUrl, productName, isProductAvailable } = useCommonProductGetters();

const primaryImage = computed(() => primaryImageData.value.path);

const showOnDisplay = computed(() => (
  (!isMcom && killswitches.isBcomBigTicketSeeItInStoreEnabled)
    || (isMcom && killswitches.isMcomBigTicketSeeItInStoreEnabled)
));

const containerId = computed(() => `image-container-${props.productId}`);

const isProtectionPlanEligible = computed(() => product?.detail?.flags.protectionPlanEligible);

const { quantity, minQuantity, maxQuantity } = useQuantity();

const margin = productHasEngagementPill ? '' : 'margin-bottom-m';

const isMcomMarketplace = marketPlaceItemEnabled && isMcom && isProductAvailable;

const imageIntrinsicWidth = 328;
/**
 * Auto replenishment
 * Using the useAutoReplenishmentProvide composable to provide the auto replenishment state
 */
useAutoReplenishmentProvide(autoReplenishmentEnabled);
</script>

<style lang="scss" scoped>
.order-panel {
  display: flex;
  flex-flow: column nowrap;
}
.image-container {
  position: relative;
}
.wishlist {
  position: absolute;
  top: 0;
  right: 14px;
  cursor: pointer;
}
.product-link {
  text-decoration: none;
}

@include breakpoint(large up) {
  .order-panel {
    flex-flow: row nowrap;
  }
  .member-item {
    flex-flow: row wrap;
  }
  .product-info {
    width: 50%;
  }
  .add-to-cart,
  .unavailable-details {
    width: 50%;
    padding-left: 12.5%;
  }
}
</style>
